import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalTitle, ModalBody, ModalFooter } from './Modal';

function safeHttpUrl(value) {
  try {
    const url = new URL(String(value || '').trim());
    if (url.protocol === 'http:' || url.protocol === 'https:') return url.href;
  } catch (_) {
    return null;
  }
  return null;
}

const BlockList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TextBlock = styled.p`
  margin: 0;
  white-space: pre-wrap;
  color: #334155;
`;

const ImageBlock = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 12px;
`;

const ImageFallback = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 14px;
`;

const LinkBlock = styled.a`
  color: #1e3a8a;
  font-weight: 600;
  text-decoration: underline;
  word-break: break-word;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #1e3a8a 0%, rgb(19, 85, 154) 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin: 0 0 12px;
  color: #b91c1c;
  font-size: 14px;
`;

const AvisoModal = ({ aviso, isOpen, saving, error, onRead }) => {
  const minRead = Math.max(0, Number(aviso?.min_read_seconds) || 0);
  const [remaining, setRemaining] = useState(minRead);
  const [brokenImages, setBrokenImages] = useState({});

  useEffect(() => {
    if (!isOpen || !aviso?.id) return undefined;
    setRemaining(minRead);
    setBrokenImages({});
    if (minRead === 0) return undefined;

    const started = Date.now();
    const timer = setInterval(() => {
      const left = Math.max(0, minRead - Math.floor((Date.now() - started) / 1000));
      setRemaining(left);
      if (left <= 0) clearInterval(timer);
    }, 250);

    return () => clearInterval(timer);
  }, [isOpen, aviso?.id, minRead]);

  if (!aviso) return null;

  const requireAck = !!aviso.require_ack;
  const locked = remaining > 0 || saving;
  const canDismiss = !requireAck && !locked;
  const blocks = Array.isArray(aviso.blocks) ? aviso.blocks : [];

  let buttonLabel = requireAck ? 'Confirmar leitura' : 'Fechar';
  if (remaining > 0) buttonLabel = `${buttonLabel} (${remaining}s)`;
  else if (saving) buttonLabel = 'Salvando...';

  return (
    <Modal
      isOpen={isOpen}
      onClose={canDismiss ? onRead : () => {}}
      closeOnOverlayClick={canDismiss}
      showCloseButton={canDismiss}
      gateBlocker={false}
    >
      <ModalTitle>{aviso.title}</ModalTitle>
      <ModalBody>
        <BlockList>
          {blocks.map((block, index) => {
            if (!block || typeof block !== 'object') return null;

            if (block.type === 'text' && block.text) {
              return <TextBlock key={`text-${index}`}>{block.text}</TextBlock>;
            }

            if (block.type === 'image') {
              const url = safeHttpUrl(block.url);
              if (!url) return null;
              if (brokenImages[index]) {
                return (
                  <ImageFallback key={`image-${index}`}>
                    {block.alt || 'Imagem indisponível'}
                  </ImageFallback>
                );
              }
              return (
                <ImageBlock
                  key={`image-${index}`}
                  src={url}
                  alt={block.alt || ''}
                  onError={() => {
                    setBrokenImages((current) => ({ ...current, [index]: true }));
                  }}
                />
              );
            }

            if (block.type === 'link') {
              const url = safeHttpUrl(block.url);
              if (!url || !block.label) return null;
              return (
                <LinkBlock
                  key={`link-${index}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {block.label}
                </LinkBlock>
              );
            }

            return null;
          })}
        </BlockList>
      </ModalBody>
      {error ? <ErrorText>{error}</ErrorText> : null}
      <ModalFooter>
        <ActionButton type="button" disabled={locked} onClick={locked ? undefined : onRead}>
          {buttonLabel}
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default AvisoModal;
